import { create } from "zustand";
import { getImages } from "api/useGetImages";
import { convertWeight } from "../receive/receive";

export const useWarehouse = create((set) => ({
  takeback: null,
  packagePictures: [],
  damagedPackagingPictures: [],
  requiresBoxDamageEvidence: false,
  hasCapturedBoxDamageEvidence: false,
  hasCheckedContents: false,
  contentPictures: [],
  nonConformPictures: [],
  controlWeight: Number(localStorage.getItem("controlWeight")) || "",
  packagingWeight: "",
  contentWeight: "",
  assembledWeight: "",
  conformingWeight: "",
  sortedPictures: {},
  rejectedContentsPictures: [],
  scaleReadingPictures: [],
  rejectCategory: "",
  selectedBucket: null,
  cachedReturnMethod: null,

  setTakeback: (takeback) => {
    // Cache the return method when setting takeback
    if (takeback) {
      const data = takeback.data
        ? typeof takeback.data === "string"
          ? JSON.parse(takeback.data)
          : takeback.data
        : null;
      set({ takeback, cachedReturnMethod: data?.returnMethod || null });
    } else {
      set({ takeback });
    }
  },
  setPackagePictures: (packagePictures) => set({ packagePictures }),
  setDamagedPackagingPictures: (damagedPackagingPictures) =>
    set({ damagedPackagingPictures }),
  setRequiresBoxDamageEvidence: (requiresBoxDamageEvidence) =>
    set({ requiresBoxDamageEvidence }),
  setHasCapturedBoxDamageEvidence: (hasCapturedBoxDamageEvidence) =>
    set({ hasCapturedBoxDamageEvidence }),
  setHasCheckedContents: (hasCheckedContents) => set({ hasCheckedContents }),
  setContentPictures: (contentPictures) => set({ contentPictures }),
  setNonConformPictures: (nonConformPictures) => set({ nonConformPictures }),
  setControlWeight: (controlWeight) => {
    localStorage.setItem("controlWeight", JSON.stringify(controlWeight));
    set({ controlWeight });
  },
  setPackagingWeight: (packagingWeight) => set({ packagingWeight }),
  setContentWeight: (contentWeight) => set({ contentWeight }),
  setAssembledWeight: (assembledWeight) => set({ assembledWeight }),
  setConformingWeight: (conformingWeight) => set({ conformingWeight }),
  setSortedPictures: (sortedPictures) => {
    set({ sortedPictures });
  },
  setRejectedContentsPictures: (rejectedContentsPictures) =>
    set({ rejectedContentsPictures }),
  setScaleReadingPictures: (scaleReadingPictures) =>
    set({ scaleReadingPictures }),
  setRejectCategory: (rejectCategory) => set({ rejectCategory }),
  setSelectedBucket: (selectedBucket) => set({ selectedBucket }),

  reset: () => {
    set({
      takeback: null,
      packagePictures: [],
      damagedPackagingPictures: [],
      requiresBoxDamageEvidence: false,
      hasCheckedContents: false,
      contentPictures: [],
      nonConformPictures: [],
      packagingWeight: "",
      contentWeight: "",
      assembledWeight: "",
      conformingWeight: "",
      sortedPictures: {},
      rejectedContentsPictures: [],
      scaleReadingPictures: [],
      rejectCategory: "",
      selectedBucket: null,
      // Note: We keep controlWeight as it persists in localStorage
    });
  },
}));

export const populateWarehouseDataFromTakeback = (takeback) => {
  if (!takeback) return;

  const data =
    typeof takeback?.data === "object"
      ? takeback?.data
      : JSON.parse(takeback?.data || "{}");

  // Use control weight from takeback data if available, otherwise from localStorage
  const controlWeight =
    data.control_weight !== undefined
      ? Number(data.control_weight)
      : Number(localStorage.getItem("controlWeight")) || 0;

  // Update the control weight in the state
  useWarehouse.setState({ controlWeight });

  // Create a converter that reverses what convertWeight does
  const fromStoredWeight = (weight) => {
    if (!weight && weight !== 0) return "";
    // If uom is "lbs", weights were stored in lbs and need conversion back to kg
    if (data.uom === "lbs") {
      // Convert from lbs to kg
      const converted = weight / 2.20462;
      // Round to 3 decimal places then to nearest integer
      // This helps recover the original kg value despite the Math.ceil in forward conversion
      const rounded = Math.round(converted * 1000) / 1000;
      // If very close to an integer (within 0.03), round to that integer
      const nearestInt = Math.round(rounded);
      if (Math.abs(rounded - nearestInt) < 0.03) {
        return nearestInt;
      }
      return Math.round(rounded * 100) / 100;
    }
    // Otherwise weights were stored in kg
    return weight;
  };

  // The weights stored in DB are ACTUAL weights (exc. control weight)
  // We need to add control weight back to get the full weights
  // Check for drop store mode fields first, then mail-in mode fields for presort warehouse weight
  const presortWeight =
    data.dropoff_presort_warehouse_weight ?? data.presort_warehouse_weight;
  if (presortWeight !== undefined && presortWeight !== null) {
    const actualContentWeight = fromStoredWeight(presortWeight);
    useWarehouse.setState({
      contentWeight: actualContentWeight + controlWeight,
    });
  }

  // Check for drop store mode fields first, then mail-in mode fields for postsort weight
  const postsortWeight = data.dropoff_conform_weight ?? data.postsort_weight;
  if (postsortWeight !== undefined && postsortWeight !== null) {
    const actualConformingWeight = fromStoredWeight(postsortWeight);
    useWarehouse.setState({
      conformingWeight: actualConformingWeight + controlWeight,
    });
  }

  if (
    data.assembled_nonconform_weight !== undefined &&
    data.assembled_nonconform_weight !== null
  ) {
    const actualAssembledWeight = fromStoredWeight(
      data.assembled_nonconform_weight
    );
    useWarehouse.setState({
      assembledWeight: actualAssembledWeight + controlWeight,
    });
  }

  // Also populate the reject category if it exists
  if (data.rejectCategory) {
    useWarehouse.setState({ rejectCategory: data.rejectCategory });
  }

  // Populate packaging weight if it exists (for drop store mode)
  if (
    data.dropoff_packaging_weight !== undefined &&
    data.dropoff_packaging_weight !== null
  ) {
    useWarehouse.setState({ packagingWeight: data.dropoff_packaging_weight });
  }
};

export const applyImageUpdates = async (accessToken, takeback) => {
  if (!takeback) {
    return;
  }

  const { images } = await getImages({ accessToken, caseId: takeback.id });

  const packagePictures = [];
  const damagedPackagingPictures = [];
  const contentPictures = [];
  const nonConformPictures = [];
  const sortedPictures = {};
  const scaleReadingPictures = [];

  for (const image of images) {
    const imageUrl = image.url;
    // fetch the image data
    const imageData = await fetch(imageUrl);
    const imageBlob = await imageData.blob();
    const imageObject = URL.createObjectURL(imageBlob);

    if (image?.fileName?.includes("v2_package_images")) {
      packagePictures.push(imageObject);
    } else if (image?.fileName?.includes("damaged_packaging_images")) {
      damagedPackagingPictures.push(imageObject);
    } else if (image?.fileName?.includes("content_images")) {
      contentPictures.push(imageObject);
    } else if (image?.fileName?.includes("non_conform_images")) {
      nonConformPictures.push(imageObject);
    } else if (image?.fileName?.includes("scale_reading")) {
      scaleReadingPictures.push(imageObject);
    } else if (
      image?.fileName?.includes("bucket") ||
      image?.fileName?.includes("_all_")
    ) {
      // Extract just the key part from the fileName
      // Format: TB-xxx_<key>_0_timestamp.jpg where <key> is "all" or "bucket2_no_conforming" etc.
      const fileNameParts = image.fileName.split("/").pop(); // Get last part if path
      const keyMatch = fileNameParts.match(
        /_(all|bucket\d+[a-z]*_[a-z_]+)_\d+_/
      );
      const key = keyMatch ? keyMatch[1] : image.fileName;
      sortedPictures[key] = imageObject;
    }
  }

  // Update all images at once
  useWarehouse.setState({
    packagePictures,
    damagedPackagingPictures,
    contentPictures,
    nonConformPictures,
    sortedPictures,
    scaleReadingPictures,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
};

export const checkForAutoEscalation = () => {
  const { takeback, conformingWeight } = useWarehouse.getState();
  const data =
    typeof takeback?.data === "object"
      ? takeback?.data
      : JSON.parse(takeback?.data || "{}");
  const weightConverter = convertWeight(data.uom);
  const conformingWeight_lbs = weightConverter(conformingWeight);
  const autoEscalate = conformingWeight_lbs < data.weight * 0.5;

  return autoEscalate;
};
